/**
 * set 化参数存取模块
 *
 * 项目整体介绍
 * - 外卖SET化综合项目：https://wiki.sankuai.com/pages/viewpage.action?pageId=1021002659
 *
 * 后端Wiki
 * - waimai_e改造：https://wiki.sankuai.com/pages/viewpage.action?pageId=1153302839#BW-连锁订单中心-版本控制-V2.11-SET化方案设计-waimai_e改造
 */

import { getCookie, setCookie } from '@utiljs/cookie';
import { updateURLParam } from '@utiljs/param';

/**
 * 存储 SET 化参数的 cookie 字段
 */
const COOKIE_KEY_SET = 'set_info';

export type SetParams =
    | null
    | { ignoreSetRouterProxy: boolean }
    | { region_id: string; region_version: string };

interface IGetParamsOptions {
    /**
     * 是否强制访问中心 SET
     */
    isForceToCenter?: boolean;
}

/**
 * 读取 SET 化参数
 *
 * @param options
 */
const getParams = (options: IGetParamsOptions = { isForceToCenter: false }): SetParams => {
    if (options.isForceToCenter) {
        return { ignoreSetRouterProxy: true };
    }

    let savedParams: any = null;

    try {
        const setInfoCookie = getCookie(COOKIE_KEY_SET);
        if (setInfoCookie !== '') {
            try {
                savedParams = JSON.parse(decodeURIComponent(setInfoCookie));
            } catch (e) {
                savedParams = JSON.parse(setInfoCookie);
            }
            if (typeof savedParams === 'string') {
                savedParams = JSON.parse(savedParams);
            }
        }
    } catch (e) {
        console.warn('SET 化参数获取失败'); // eslint-disable-line no-console
    }
    if (savedParams === null) {
        return null;
    }

    const wmPoiId = getCookie('wmPoiId');
    const savedWmPoiId = String(savedParams.wmPoiId);
    if (wmPoiId !== savedWmPoiId) {
        console.warn('SET 化参数门店 ID 不匹配'); // eslint-disable-line no-console
        return null;
    }

    // 保证只有SET化参数，且不会有空值
    if (savedParams.ignoreSetRouterProxy === true) {
        return {
            ignoreSetRouterProxy: true,
        };
    } else if (savedParams.region_id && savedParams.region_version) {
        return {
            region_id: savedParams.region_id,
            region_version: savedParams.region_version,
        };
    }
    return null;
};

type NewSetParams =
    | null
    | { wmPoiId: string | number; ignoreSetRouterProxy: boolean }
    | { wmPoiId: string | number; region_id: string | number; region_version: string | number }
    | {
          wmPoiId: string | number;
          region_id: string | number;
          region_version: string | number;
          ignoreSetRouterProxy: boolean;
      };

/**
 * 写入 SET 化参数
 *
 * @param params
 */
const setParams = (params: NewSetParams): void => {
    if (params === null) {
        return;
    }

    let paramsToSave: any;

    if ('ignoreSetRouterProxy' in params && params.ignoreSetRouterProxy) {
        paramsToSave = {
            wmPoiId: params.wmPoiId,
            ignoreSetRouterProxy: true,
        };
    } else if (
        'region_id' in params &&
        'region_version' in params &&
        params.region_id &&
        params.region_version
    ) {
        paramsToSave = {
            wmPoiId: params.wmPoiId,
            region_id: params.region_id,
            region_version: params.region_version,
        };
    }

    if (paramsToSave) {
        setCookie(COOKIE_KEY_SET, JSON.stringify(paramsToSave), {
            expires: new Date(new Date().getTime() + 24 * 30 * 60 * 60 * 1000),
        });
    }
};

const appendSetParamsToUrl = (url: string): string => {
    const params = getParams();
    let finalUrl = url;

    if (params) {
        if ('ignoreSetRouterProxy' in params) {
            if (params.ignoreSetRouterProxy) {
                finalUrl = updateURLParam(finalUrl, 'ignoreSetRouterProxy', 'true');
            }
        } else {
            finalUrl = updateURLParam(finalUrl, 'region_id', params.region_id);
            finalUrl = updateURLParam(finalUrl, 'region_version', params.region_version);
        }
    }

    return finalUrl;
};

const set = {
    getParams,
    setParams,
    appendSetParamsToUrl,
};

export default set;
export { NewSetParams };
